import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Search, CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button, Input, cn } from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import { getPortalUrl } from '../config/portal';
import { marketingCta, marketingCtaText } from './buttonStyles';

/**
 * DomainSearch — no-commitment domain availability search (2026-07-02).
 *
 * Calls the public `domain-availability` Edge Function directly (anonymous;
 * CORS-cleared for this origin; per-IP rate-limited server-side). Two
 * variants:
 *
 * - `hero`  — full card for the HomePage / DomainsPage hero: TLD chips,
 *             alternative-TLD suggestions when the name is taken.
 * - `strip` — slim single-row band mounted site-wide by RootLayout so the
 *             search is at the front of every page without competing with
 *             page content.
 *
 * CTA routing follows the concierge domain model (DT-1): `.za` family TLDs
 * are self-serve (portal register funnel, R99/yr flat); verified gTLDs show
 * availability live but route to /contact for team-assisted registration
 * until wholesale pricing is confirmed (see siteConfig.domainSearch).
 */

const ENDPOINT = siteConfig.domainAvailabilityEndpoint;
const TLD_CATALOG = siteConfig.domainSearch.tlds;
// Widen the `as const` literals so chip selection / alternate lists type as
// plain strings.
const FEATURED: readonly string[] = siteConfig.domainSearch.featuredTlds;
const DEFAULT_TLD: string = siteConfig.domainSearch.defaultTld;

const SLD_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

interface CheckResult {
  fqdn: string;
  state: 'available' | 'taken' | 'unsupported' | 'rate_limited' | 'error';
  message?: string;
}

interface Alternate {
  fqdn: string;
  tld: string;
}

function tldMeta(tld: string) {
  return TLD_CATALOG.find((t) => t.tld === tld.toLowerCase()) ?? null;
}

/** "mybrand", "www.mybrand.co.za", "https://mybrand.com/" → clean fqdn. */
function normalise(raw: string, fallbackTld: string): string | null {
  let v = raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '')
    .replace(/\.+$/, '');
  if (!v) return null;
  if (!v.includes('.')) v = `${v}.${fallbackTld}`;
  const sld = v.split('.')[0] ?? '';
  return SLD_RE.test(sld) ? v : null;
}

function splitTld(fqdn: string): string {
  // Longest catalog TLD that suffix-matches wins (handles co.za vs za).
  const match = TLD_CATALOG
    .filter((t) => fqdn.endsWith(`.${t.tld}`))
    .sort((a, b) => b.tld.length - a.tld.length)[0];
  return match ? match.tld : fqdn.split('.').slice(1).join('.');
}

async function checkDomain(fqdn: string): Promise<CheckResult> {
  try {
    const res = await fetch(`${ENDPOINT}?domain=${encodeURIComponent(fqdn)}`);
    if (res.ok) {
      const body = (await res.json()) as { available: boolean };
      return { fqdn, state: body.available ? 'available' : 'taken' };
    }
    if (res.status === 429) {
      return {
        fqdn,
        state: 'rate_limited',
        message: 'Lots of searches from your network — please try again in a few minutes.',
      };
    }
    const body = (await res.json().catch(() => null)) as { code?: string; message?: string } | null;
    if (body?.code === 'tld_not_supported') {
      return { fqdn, state: 'unsupported', message: body.message };
    }
    return { fqdn, state: 'error' };
  } catch {
    return { fqdn, state: 'error' };
  }
}

/** CTA pair for an available domain, sized for the hero or strip context. */
function AvailableCtas({ fqdn, compact }: { fqdn: string; compact?: boolean }) {
  const tld = splitTld(fqdn);
  const meta = tldMeta(tld);
  const size = compact ? 'sm' : 'lg';
  if (meta?.selfServe) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={getPortalUrl(
            siteConfig.portal.domainRegisterPath,
            { medium: 'domain-search', campaign: 'domains', content: tld },
            { domain: fqdn },
          )}
        >
          <Button size={size} className={cn(marketingCta.primary, marketingCtaText[compact ? 'sm' : 'lg'])}>
            Register — R{meta.priceZar}/yr
          </Button>
        </a>
        <a
          href={getPortalUrl(
            siteConfig.portal.hostingOrderPath,
            { medium: 'domain-search', campaign: 'hosting', content: tld },
            { domain: fqdn },
          )}
        >
          <Button size={size} className={cn(marketingCta.ghost, marketingCtaText[compact ? 'sm' : 'lg'])}>
            {/* Free-domain-with-hosting is a .co.za-only marketing promise. */}
            {tld === 'co.za' ? 'FREE with hosting' : 'Get it with hosting'}
          </Button>
        </a>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Link to={`/contact?domain=${encodeURIComponent(fqdn)}`}>
        <Button size={size} className={cn(marketingCta.primary, marketingCtaText[compact ? 'sm' : 'lg'])}>
          {meta?.priceZar ? `Order — from R${meta.priceZar}/yr` : 'Order via our team'}
        </Button>
      </Link>
      {!compact && (
        <span className="text-xs text-white/50">
          Registered for you by our team — typically within the hour.
        </span>
      )}
    </div>
  );
}

interface DomainSearchProps {
  variant?: 'hero' | 'strip';
  /** Auto-run a search on mount (e.g. /domains?search=name). */
  initialQuery?: string;
  className?: string;
}

export function DomainSearch({ variant = 'hero', initialQuery, className }: DomainSearchProps) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [activeTld, setActiveTld] = useState(DEFAULT_TLD);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [alternates, setAlternates] = useState<Alternate[] | null>(null);
  const [invalid, setInvalid] = useState(false);
  // Serial number guards against out-of-order responses from rapid re-submits.
  const searchSeq = useRef(0);

  const runSearch = useCallback(
    async (raw: string, tld: string) => {
      const fqdn = normalise(raw, tld);
      if (!fqdn) {
        setInvalid(true);
        return;
      }
      const seq = ++searchSeq.current;
      setInvalid(false);
      setChecking(true);
      setResult(null);
      setAlternates(null);

      const primary = await checkDomain(fqdn);
      if (seq !== searchSeq.current) return;
      setResult(primary);
      setChecking(false);

      // Hero variant: when taken, suggest the same name on alternative TLDs
      // (the xneelo/GoDaddy recovery pattern — where lost searches convert).
      if (variant === 'hero' && primary.state === 'taken') {
        const sld = fqdn.split('.')[0]!;
        const searchedTld = splitTld(fqdn);
        const altTlds = FEATURED.filter((t) => t !== searchedTld).slice(0, 3);
        const checks = await Promise.all(
          altTlds.map(async (t) => {
            const alt = await checkDomain(`${sld}.${t}`);
            return alt.state === 'available' ? { fqdn: alt.fqdn, tld: t } : null;
          }),
        );
        if (seq !== searchSeq.current) return;
        setAlternates(checks.filter((a): a is Alternate => a !== null));
      }
    },
    [variant],
  );

  useEffect(() => {
    if (initialQuery) void runSearch(initialQuery, DEFAULT_TLD);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only auto-run
  }, []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void runSearch(query, activeTld);
  };

  const resultBlock = result && (
    <div
      aria-live="polite"
      className={cn(
        'space-y-3 rounded-soft border p-4',
        result.state === 'available' && 'border-emerald-500/40 bg-emerald-500/10',
        result.state === 'taken' && 'border-white/15 bg-white/5',
        (result.state === 'unsupported' || result.state === 'error' || result.state === 'rate_limited') &&
          'border-amber-500/40 bg-amber-500/10',
        variant === 'strip' && 'p-3',
      )}
    >
      {result.state === 'available' && (
        <>
          <p className="flex items-center gap-2 font-semibold text-emerald-400">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <span>
              <span className="text-white">{result.fqdn}</span> is available!
            </span>
          </p>
          <AvailableCtas fqdn={result.fqdn} compact={variant === 'strip'} />
        </>
      )}

      {result.state === 'taken' && (
        <>
          <p className="flex items-center gap-2 text-white/80">
            <XCircle className="h-5 w-5 shrink-0 text-red-400" />
            <span>
              <span className="font-semibold text-white">{result.fqdn}</span> is already registered.
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={getPortalUrl(
                siteConfig.portal.domainTransferPath,
                { medium: 'domain-search', campaign: 'domains', content: 'transfer' },
                { domain: result.fqdn },
              )}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              Is it yours? Transfer it to SightHost <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          {alternates && alternates.length > 0 && (
            <div className="border-t border-white/10 pt-3">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                Also available
              </p>
              <ul className="space-y-1.5">
                {alternates.map((alt) => {
                  const meta = tldMeta(alt.tld);
                  return (
                    <li key={alt.fqdn} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="flex items-center gap-2 text-white">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        {alt.fqdn}
                        {meta?.priceZar && (
                          <span className="text-white/50">
                            {meta.selfServe ? `R${meta.priceZar}/yr` : `from R${meta.priceZar}/yr`}
                          </span>
                        )}
                      </span>
                      <a
                        href={
                          meta?.selfServe
                            ? getPortalUrl(
                                siteConfig.portal.domainRegisterPath,
                                { medium: 'domain-search', campaign: 'domains', content: alt.tld },
                                { domain: alt.fqdn },
                              )
                            : `/contact?domain=${encodeURIComponent(alt.fqdn)}`
                        }
                        className="font-medium text-primary hover:underline"
                      >
                        {meta?.selfServe ? 'Register' : 'Order'}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}

      {result.state === 'unsupported' && (
        <p className="text-sm text-white/80">
          {result.message ?? 'That extension isn’t available yet.'}{' '}
          <Link to={`/contact?domain=${encodeURIComponent(result.fqdn)}`} className="font-medium text-primary hover:underline">
            Ask us about it →
          </Link>
        </p>
      )}

      {result.state === 'rate_limited' && (
        <p className="text-sm text-white/80">{result.message}</p>
      )}

      {result.state === 'error' && (
        <p className="text-sm text-white/80">
          We couldn’t reach the registry just now — please try again, or{' '}
          <Link to="/contact" className="font-medium text-primary hover:underline">
            ask our team
          </Link>
          .
        </p>
      )}
    </div>
  );

  if (variant === 'strip') {
    return (
      <div className={cn('border-b border-white/10 bg-neutral-900/60', className)}>
        <div className="container mx-auto px-6 py-3">
          <form onSubmit={onSubmit} className="flex flex-wrap items-center gap-3">
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 sm:inline">
              Find your domain
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Input
                type="text"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                placeholder="yourname.co.za"
                aria-label="Search for a domain name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 min-w-0 flex-1 border-white/15 bg-neutral-950/70 text-sm text-white placeholder:text-white/35"
              />
              <Button
                type="submit"
                size="sm"
                disabled={checking}
                className={cn(marketingCta.primary, marketingCtaText.sm, 'shrink-0')}
              >
                {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-1.5 hidden sm:inline">Search</span>
              </Button>
            </div>
            <span className="hidden text-xs text-white/55 md:inline">
              .co.za <span className="text-primary">R99/yr</span> — flat, every year
            </span>
          </form>
          {invalid && (
            <p className="pt-2 text-xs text-amber-400">
              Use letters, digits and hyphens — e.g. <span className="font-mono">yourname.co.za</span>
            </p>
          )}
          {resultBlock && <div className="pt-3">{resultBlock}</div>}
        </div>
      </div>
    );
  }

  // hero variant
  return (
    <div
      className={cn(
        'w-full max-w-xl rounded-soft border border-white/12 bg-neutral-950/70 p-6 shadow-2xl backdrop-blur-md md:p-7',
        className,
      )}
    >
      <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
        Domain search
      </p>
      <h2 className="mb-4 font-display text-xl font-bold text-white md:text-2xl">
        Is your name still free?
      </h2>

      <form onSubmit={onSubmit} className="flex items-stretch gap-2">
        <Input
          type="text"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder={`yourname.${activeTld}`}
          aria-label="Search for a domain name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-12 min-w-0 flex-1 border-white/15 bg-neutral-900/80 text-base text-white placeholder:text-white/35"
        />
        <Button
          type="submit"
          size="lg"
          disabled={checking}
          className={cn(marketingCta.primary, marketingCtaText.lg, 'shrink-0 px-5')}
        >
          {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          <span className="ml-2 hidden sm:inline">Search</span>
        </Button>
      </form>

      {invalid && (
        <p className="pt-2 text-xs text-amber-400">
          Use letters, digits and hyphens — e.g. <span className="font-mono">yourname.co.za</span>
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {FEATURED.map((tld) => {
          const meta = tldMeta(tld);
          const active = tld === activeTld;
          return (
            <button
              key={tld}
              type="button"
              onClick={() => setActiveTld(tld)}
              aria-pressed={active}
              className={cn(
                'rounded-full border px-3 py-1 font-mono text-[11px] transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950',
                active
                  ? 'border-primary/60 bg-primary/15 text-primary'
                  : 'border-white/12 text-white/55 hover:border-white/30 hover:text-white/80',
              )}
            >
              .{tld}
              {meta?.priceZar ? (
                <span className={active ? 'text-primary/80' : 'text-white/50'}>
                  {' '}
                  R{meta.priceZar}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {resultBlock && <div className="mt-4">{resultBlock}</div>}

      <p className="mt-4 text-[11px] leading-relaxed text-white/55">
        Live registry answer — no sign-up needed. .co.za is R99 today and R99 every year.
      </p>
    </div>
  );
}
